import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDownLeftIcon,
  ArrowSquareOutIcon,
  ChartLineUpIcon,
  CoinIcon,
  QuestionIcon,
  StorefrontIcon,
  TrendDownIcon,
  TrendUpIcon,
  WarningIcon,
} from '@phosphor-icons/react/dist/ssr';

import { AuthLoadingScreen } from '@/components/templates';
import { ScreenShell } from '@/components/screen-shell';
import { NavBar } from '@/components/nav-bar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { buttonVariants } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardEyebrow,
  CardHeader,
} from '@/components/ui/card';
import { KeyValue } from '@/components/ui/key-value';
import { cn } from '@/lib/utils';

import { listPositions, type PositionSummary } from '../api';
import { useAuth } from '../auth';
import { useT, type TFunction } from '../i18n';
import { useTelegramBackButton } from '../lib/telegram-back-button';
import { useNavStackEntry } from '../lib/nav-stack';

function fmtUsd(n: number): string {
  if (!Number.isFinite(n)) return '$0.00';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  return `${sign}$${abs.toFixed(2)}`;
}

function fmtCents(p: number): string {
  if (!Number.isFinite(p) || p <= 0) return '—';
  return `${(p * 100).toFixed(1)}\u00a2`;
}

function fmtDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function Portfolio() {
  const t = useT();
  const auth = useAuth();
  useTelegramBackButton();
  useNavStackEntry({ label: t('portfolio.breadcrumb') });

  const externalUserId =
    auth.state === 'ready' ? auth.auth.externalUserId : null;

  const [positions, setPositions] = useState<PositionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!externalUserId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await listPositions(externalUserId, {
          sizeThreshold: 0.01,
          sortBy: 'CURRENT',
        });
        if (!cancelled) setPositions(data);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [externalUserId]);

  if (auth.state === 'loading' || (loading && positions === null)) {
    return (
      <AuthLoadingScreen
        title={t('portfolio.loading_title')}
        description={t('portfolio.loading_desc')}
        shortDescription={t('portfolio.loading_short')}
        steps={[
          { label: 'Session', color: 'bg-emerald-500' },
          { label: t('portfolio.eyebrow'), color: 'bg-primary', delay: '0.4s' },
        ]}
      />
    );
  }

  if (auth.state === 'error') {
    return (
      <ScreenShell
        eyebrow={
          <span className="inline-flex items-center gap-1.5">
            <ChartLineUpIcon weight="fill" className="size-3" />
            {t('portfolio.eyebrow')}
          </span>
        }
        title={t('portfolio.title')}
      >
        <NavBar />
        <Alert tone="danger">
          <WarningIcon weight="fill" className="size-4" />
          <AlertTitle>{t('common.auth_error', { error: '' })}</AlertTitle>
          <AlertDescription>{auth.error}</AlertDescription>
        </Alert>
      </ScreenShell>
    );
  }

  if (error) {
    return (
      <ScreenShell
        eyebrow={
          <span className="inline-flex items-center gap-1.5">
            <ChartLineUpIcon weight="fill" className="size-3" />
            {t('portfolio.eyebrow')}
          </span>
        }
        title={t('portfolio.title')}
      >
        <NavBar />
        <Alert tone="danger">
          <WarningIcon weight="fill" className="size-4" />
          <AlertTitle>{t('portfolio.error_title')}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </ScreenShell>
    );
  }

  if (!positions || positions.length === 0) {
    return (
      <ScreenShell
        eyebrow={
          <span className="inline-flex items-center gap-1.5">
            <ChartLineUpIcon weight="fill" className="size-3" />
            {t('portfolio.eyebrow')}
          </span>
        }
        title={t('portfolio.empty_title')}
        description={t('portfolio.empty_intro')}
      >
        <NavBar />
        <Card>
          <CardContent>
            <ol className="text-foreground flex flex-col gap-3 text-[14px] leading-relaxed">
              <li>{t('portfolio.empty_step_1')}</li>
              <li>{t('portfolio.empty_step_2')}</li>
              <li>{t('portfolio.empty_step_3')}</li>
            </ol>
          </CardContent>
        </Card>
        <div className="flex flex-col gap-2">
          <Link
            to="/deposit"
            className={cn(buttonVariants({ size: 'lg' }), 'justify-center')}
          >
            <ArrowDownLeftIcon weight="bold" className="size-4" />
            {t('portfolio.empty_cta_deposit')}
          </Link>
          <Link
            to="/markets"
            className={cn(
              buttonVariants({ variant: 'outline', size: 'lg' }),
              'justify-center',
            )}
          >
            <StorefrontIcon weight="bold" className="size-4" />
            {t('portfolio.empty_cta_markets')}
          </Link>
          <Link
            to="/help"
            className={cn(
              buttonVariants({ variant: 'ghost', size: 'sm' }),
              'justify-center',
            )}
          >
            <QuestionIcon weight="bold" className="size-4" />
            {t('portfolio.empty_cta_help')}
          </Link>
        </div>
      </ScreenShell>
    );
  }

  const totals = positions.reduce(
    (acc, p) => {
      acc.payout += Number(p.size) || 0;
      acc.value += Number(p.currentValue) || 0;
      acc.pnl += Number(p.cashPnl) || 0;
      return acc;
    },
    { payout: 0, value: 0, pnl: 0 },
  );

  return (
    <ScreenShell
      eyebrow={
        <span className="inline-flex items-center gap-1.5">
          <ChartLineUpIcon weight="fill" className="size-3" />
          {t('portfolio.eyebrow')}
        </span>
      }
      title={t('portfolio.title')}
      description={t('portfolio.description')}
    >
      <NavBar />

      <Card>
        <CardHeader>
          <CardEyebrow>
            <CoinIcon weight="fill" className="size-3" />
            {t('portfolio.totals_title')}
          </CardEyebrow>
        </CardHeader>
        <CardContent className="grid grid-cols-3 gap-2">
          <KeyValue
            label={t('portfolio.payout_label')}
            value={fmtUsd(totals.payout)}
            mono
          />
          <KeyValue
            label={t('portfolio.value_label')}
            value={fmtUsd(totals.value)}
            mono
          />
          <KeyValue
            label={t('portfolio.pnl_label')}
            value={
              <span
                className={cn(
                  totals.pnl > 0 && 'text-emerald-500',
                  totals.pnl < 0 && 'text-rose-500',
                )}
              >
                {fmtUsd(totals.pnl)}
              </span>
            }
            mono
          />
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3">
        {positions.map((p) => (
          <PositionCard key={`${p.conditionId}:${p.outcomeIndex}`} position={p} t={t} />
        ))}
      </div>
    </ScreenShell>
  );
}

function PositionCard({
  position,
  t,
}: {
  position: PositionSummary;
  t: TFunction;
}) {
  const size = Number(position.size) || 0;
  const avg = Number(position.avgPrice) || 0;
  const cur = Number(position.curPrice) || 0;
  const value = Number(position.currentValue) || size * cur;
  const payout = size;
  const pnl = Number(position.cashPnl) || 0;
  const pctPnl = Number(position.percentPnl) || 0;
  const ends = fmtDate(position.endDate);
  return (
    <Link
      to={`/markets/m/${encodeURIComponent(position.marketSlug)}`}
      className="group bg-card/85 supports-backdrop-filter:bg-card/80 ring-foreground/10 hover:ring-primary/30 hover:bg-card relative flex w-full flex-col gap-3 overflow-hidden rounded-3xl p-4 ring-1 backdrop-blur-md transition-all hover:-translate-y-0.5 sm:p-5"
    >
      <div className="flex items-start gap-3">
        {position.icon ? (
          <img
            src={position.icon}
            alt=""
            loading="lazy"
            className="size-10 shrink-0 rounded-xl object-cover ring-1 ring-foreground/10"
          />
        ) : (
          <span className="bg-primary/12 ring-primary/25 inline-flex size-10 shrink-0 items-center justify-center rounded-xl ring-1">
            <StorefrontIcon weight="fill" className="text-primary size-5" />
          </span>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h3 className="text-foreground text-[15px] font-semibold leading-snug tracking-tight">
            {position.title}
          </h3>
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <Badge tone="primary">{position.outcome}</Badge>
            {position.redeemable ? (
              <Badge tone="success">{t('portfolio.redeemable_badge')}</Badge>
            ) : null}
            {ends ? (
              <span className="text-muted-foreground tabular-nums">
                {t('portfolio.resolves_label')}: {ends}
              </span>
            ) : null}
          </div>
        </div>
        <ArrowSquareOutIcon
          weight="bold"
          className="text-muted-foreground size-4 shrink-0 opacity-60 transition-opacity group-hover:opacity-100"
          aria-hidden
        />
      </div>

      <div className="grid grid-cols-4 gap-2">
        <KeyValue
          label={t('portfolio.size_label')}
          value={size.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          mono
        />
        <KeyValue
          label={t('portfolio.avg_label')}
          value={fmtCents(avg)}
          mono
        />
        <KeyValue
          label={t('portfolio.cur_label')}
          value={fmtCents(cur)}
          mono
        />
        <KeyValue
          label={t('portfolio.value_label')}
          value={fmtUsd(value)}
          mono
        />
      </div>

      <div className="flex items-center justify-between gap-3 text-[12px] tabular-nums">
        <span className="text-muted-foreground">
          {t('portfolio.payout_label')}:{' '}
          <span className="text-foreground font-semibold">
            {fmtUsd(payout)}
          </span>
        </span>
        <span
          className={cn(
            'inline-flex items-center gap-1 font-semibold',
            pnl > 0 && 'text-emerald-500',
            pnl < 0 && 'text-rose-500',
            pnl === 0 && 'text-muted-foreground',
          )}
        >
          {pnl > 0 ? (
            <TrendUpIcon weight="fill" className="size-3" />
          ) : pnl < 0 ? (
            <TrendDownIcon weight="fill" className="size-3" />
          ) : null}
          {fmtUsd(pnl)} ({pctPnl > 0 ? '+' : ''}
          {pctPnl.toFixed(1)}%)
        </span>
      </div>
    </Link>
  );
}
